/**
 * Rail-shelf rules for server folders.
 *
 * Pure on purpose: a caption has to survive a 76px-wide column, and "which
 * folders get a caption at all" is a decision worth pinning in a test rather
 * than eyeballing in the running rail.
 */
import i18n from '@/i18n';

/**
 * Caption printed above an expanded folder in the vertical rail, or null when
 * the folder should show none.
 *
 * A folder with no name falls back to `Папка · 3` elsewhere in the UI (the
 * collapsed tile's tooltip). In the rail that fallback is noise — the tile
 * already shows three icons — so an unnamed folder gets no caption and is
 * marked only by its colour spine.
 */
export function railFolderCaption(folder: { name?: string | null }): string | null {
  const name = folder.name?.trim();
  return name ? name : null;
}

/** Server-side cap on a folder name (`z.string().max(40)`). */
export const FOLDER_NAME_MAX = 40;

/** How many server names an unnamed folder lists before it trails off. */
const LABEL_NAMES = 4;

/**
 * Full folder label for tooltips and aria.
 *
 * An unnamed folder is called by what's inside it — «Siberian eSports, Лес,
 * 13's server, Dota 2 Scrims, …» — which is both what Discord does and simply
 * more useful than «Папка · 4»: the whole point of hovering a collapsed folder
 * is to find out which servers it swallowed.
 */
export function railFolderLabel(folder: { name?: string | null }, memberNames: string[]): string {
  const named = railFolderCaption(folder);
  if (named) return named;
  const names = memberNames.map((n) => n.trim()).filter(Boolean);
  if (names.length === 0) return i18n.t('lib2.folder.untitled');
  const shown = names.slice(0, LABEL_NAMES).join(', ');
  return names.length > LABEL_NAMES ? `${shown}, …` : shown;
}

/**
 * Unread count as printed on a rail badge, or null when there is nothing to
 * show. Anything past 99 collapses to `99+` — three glyphs is what fits.
 */
export function railUnreadLabel(unread: number): string | null {
  if (!Number.isFinite(unread) || unread <= 0) return null;
  return unread > 99 ? '99+' : String(Math.floor(unread));
}
